import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import {
	PositionCandidate,
	RoleRow,
	SeniorityBreakdown,
	groupSimilarRoleRows,
	extractSeniority,
} from '@/utils/rolesGrouping';
import { normalizeCompanyName } from '@/utils/companyNameNormalization';

const emptyBreakdown = (): SeniorityBreakdown => ({
	lead: 0,
	senior: 0,
	mid: 0,
	junior: 0,
	unknown: 0,
});

const MIN_CANDIDATES_PER_ROLE = 2;

export interface UseAllRolesReturn {
	roles: RoleRow[];
	loading: boolean;
	error: string | null;
	searchTerm: string;
	setSearchTerm: (term: string) => void;
	refresh: () => Promise<void>;
}

/**
 * GLOBAL ROLES HOOK (role-centric)
 *
 * Aggregates candidate CV experiences into role-titled rows, each with a
 * company breakdown showing how many distinct candidates held that role at
 * each past employer. Drives the /roles sourcing view.
 *
 * DATA SOURCES:
 * - experiences table: Direct candidate experiences
 * - candidates.ai_analysis: AI-extracted experience data
 */
export const useAllRoles = (): UseAllRolesReturn => {
	const [roles, setRoles] = useState<RoleRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const { currentOrganization } = useAuth();

	const fetchRoles = async () => {
		try {
			setLoading(true);
			setError(null);

			const companiesQuery = currentOrganization?.id
				? supabase
					.from('companies')
					.select('id, name')
					.eq('organization_uuid', currentOrganization.id)
				: Promise.resolve({ data: [], error: null });

			const [experiencesResult, candidatesResult, companiesResult] = await Promise.all([
				supabase
					.from('experiences')
					.select(`
						id,
						title,
						dates,
						company,
						candidate_id,
						candidates (
							id,
							name
						)
					`)
					.order('title', { ascending: true }),

				supabase
					.from('candidates')
					.select(`
						id,
						name,
						ai_analysis
					`)
					.not('ai_analysis', 'is', null),

				companiesQuery,
			]);

			if (experiencesResult.error)
				throw experiencesResult.error;
			if (candidatesResult.error)
				throw candidatesResult.error;

			const experiencesData = experiencesResult.data || [];

			const aiExperiencesData: any[] = [];
			if (candidatesResult.data) {
				candidatesResult.data.forEach(candidate => {
					if (candidate.ai_analysis?.roles_jobs_companies_dates?.["Extracted Value"]) {
						const aiExperiences = candidate.ai_analysis.roles_jobs_companies_dates["Extracted Value"];
						aiExperiences.forEach((exp: any) => {
							const title = exp.title || exp.role || exp.job || exp.position || '';
							if (!title) return;
							aiExperiencesData.push({
								id: `ai_${candidate.id}_${exp.company}_${title}`,
								title,
								dates: exp.dates || '',
								company: exp.company || '',
								candidate_id: candidate.id,
								candidates: {
									id: candidate.id,
									name: candidate.name
								}
							});
						});
					}
				});
			}

			const allData = [...experiencesData, ...aiExperiencesData];

			if (allData.length === 0) {
				setRoles([]);
				return;
			}

			// Build: title -> { candidateIds, perCompany: company -> { displayName, candidates } }
			interface TitleBucket {
				candidateIds: Set<string>;
				perCompany: Map<string, { displayName: string; candidates: Map<string, PositionCandidate> }>;
			}

			const titleMap = new Map<string, TitleBucket>();

			allData.forEach((experience) => {
				if (!experience.candidates) return;
				const title = (experience.title || '').trim();
				if (!title) return;
				const rawCompany = (experience.company || '').trim();
				if (!rawCompany) return;

				if (!titleMap.has(title))
					titleMap.set(title, { candidateIds: new Set(), perCompany: new Map() });
				const bucket = titleMap.get(title)!;

				const companyKey = rawCompany.toLowerCase();
				if (!bucket.perCompany.has(companyKey))
					bucket.perCompany.set(companyKey, { displayName: rawCompany, candidates: new Map() });
				const companyEntry = bucket.perCompany.get(companyKey)!;

				const candidateId = experience.candidates.id;
				const { level: seniority } = extractSeniority(title);
				if (!companyEntry.candidates.has(candidateId)) {
					const candidate: PositionCandidate = {
						id: candidateId,
						firstName: experience.candidates.name || '',
						lastName: '',
						dates: experience.dates || '',
						experienceId: experience.id,
						originalTitle: title,
						seniority,
					};
					companyEntry.candidates.set(candidateId, candidate);
				}
				bucket.candidateIds.add(candidateId);
			});

			// Convert to preliminary RoleRow[] (one per raw title)
			const preliminary: RoleRow[] = Array.from(titleMap.entries()).map(([title, bucket]) => {
				const companyBreakdown = Array.from(bucket.perCompany.values()).map(({ displayName, candidates }) => ({
					company: displayName,
					candidateCount: candidates.size,
					candidates: Array.from(candidates.values()),
					seniorityBreakdown: emptyBreakdown(),
				}));
				return {
					title,
					totalCandidateCount: bucket.candidateIds.size,
					companyBreakdown,
					candidateNames: [],
					seniorityBreakdown: emptyBreakdown(),
				};
			});

			// Merge similar titles across companies; rebuilds breakdowns + totals.
			const merged = groupSimilarRoleRows(preliminary);

			// Resolve each breakdown's company name to a real companies.id (when one exists).
			const companyIdByNormName = new Map<string, string>();
			(companiesResult.data || []).forEach((c: { id: string; name: string }) => {
				const key = normalizeCompanyName(c.name);
				if (key && !companyIdByNormName.has(key))
					companyIdByNormName.set(key, c.id);
			});

			const resolved = merged.map(role => ({
				...role,
				companyBreakdown: role.companyBreakdown.map(b => ({
					...b,
					companyId: companyIdByNormName.get(normalizeCompanyName(b.company)),
				})),
			}));

			const finalRoles = resolved
				.filter(r => r.totalCandidateCount >= MIN_CANDIDATES_PER_ROLE)
				.sort((a, b) => b.totalCandidateCount - a.totalCandidateCount);

			setRoles(finalRoles);
		} catch (err) {
			console.error('Error fetching all roles:', err);
			setError(err instanceof Error ? err.message : 'Failed to fetch roles');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchRoles();
	}, []);

	const needle = searchTerm.toLowerCase();
	const filteredRoles = needle
		? roles.filter(role =>
			role.title.toLowerCase().includes(needle) ||
			role.companyBreakdown.some(b => b.company.toLowerCase().includes(needle)) ||
			role.candidateNames.some(n => n.toLowerCase().includes(needle)) ||
			role.companyBreakdown.some(b =>
				b.candidates.some(c => c.originalTitle.toLowerCase().includes(needle))
			)
		)
		: roles;

	return {
		roles: filteredRoles,
		loading,
		error,
		searchTerm,
		setSearchTerm,
		refresh: fetchRoles,
	};
};
